import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { usePermissions } from "@/hooks/usePermissions";
import AdminLayout from "@/components/admin/AdminLayout";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = ["Open", "Assigned", "In Progress", "Resolved", "Closed"];

export default function Complaints() {
  const { can } = usePermissions();
  const [items, setItems] = useState([]);
  const [reply, setReply] = useState({});
  const load = () => api.get("/complaints").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const update = async (id, body) => {
    try { await api.put(`/complaints/${id}`, body); toast.success("Updated"); setReply({ ...reply, [id]: "" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <AdminLayout title="Complaints">
      {items.length === 0 ? <div className="rounded-xl border border-dashed border-border p-12 text-center text-muted-foreground">No complaints.</div> : (
        <div className="grid gap-3">
          {items.map((c) => (
            <div key={c.id} className="rounded-xl border border-border bg-card p-4" data-testid={`admin-complaint-${c.id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <p className="font-semibold">{c.subject}</p><StatusBadge status={c.status} />
                <span className="text-xs text-muted-foreground">{c.complaint_no} · {c.customer_name} · {c.category}</span>
                {can("complaints", "edit") && (
                  <Select value={c.status} onValueChange={(v) => update(c.id, { status: v })}>
                    <SelectTrigger className="ml-auto h-8 w-36 text-xs" data-testid={`complaint-status-${c.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </div>
              <p className="mt-2 text-sm text-muted-foreground">{c.description}</p>
              {(c.replies || []).map((r, i) => <p key={i} className="mt-2 rounded-lg bg-secondary/60 px-3 py-2 text-sm"><span className="font-semibold">{r.by}:</span> {r.text}</p>)}
              {can("complaints", "edit") && (
                <div className="mt-3 flex gap-2">
                  <Input value={reply[c.id] || ""} onChange={(e) => setReply({ ...reply, [c.id]: e.target.value })} placeholder="Reply to customer..." data-testid={`complaint-reply-${c.id}`} />
                  <Button onClick={() => update(c.id, { reply: reply[c.id] })} disabled={!reply[c.id]}>Send</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
